import * as Sentry from '@sentry/react'
import { LOCAL_STORAGE_KEYS } from 'utils/constants'
import { getCaregiverSelectedParticipantId } from 'utils/events'
import { getHpiHardwareIdHeaderValue } from 'utils/hardwareId'

export const SENTRY_MESSAGES = {
  instrumentDelivered: 'Instrument Delivered',
  instanceDelivered: 'Instance Delivered',
  instrumentVisible: 'Instrument Visible',
  instrumentSeen: 'Instrument Seen',
  instrumentOpened: 'Instrument Opened',
  instrumentCompleted: 'Instrument Completed',
  instrumentSubmitted: 'Instrument Submitted',
  instrumentCancelled: 'Instrument Cancelled',
  instrumentUpdated: 'Instrument Updated',
  instrumentLoadFailed: 'Instrument Load Failed',
  instrumentSubmitFailed: 'Instrument Submit Failed',
  instrumentDownloadFailed: 'Instrument Download Failed',
  instrumentParsingFailed: 'Instrument Parsing Failed',
  pendingInstrumentsFetchFailed: 'Error fetching pending instruments',
  consentOpened: 'Consent Opened',
  consentAccepted: 'Consent Accepted',
  consentSubmitted: 'Consent Submitted',
  consentRejected: 'Consent Rejected',
  consentDownloaded: 'Consent Downloaded',
  consentStartSubmit: 'Consent Start Submit',
  consentStartDownload: 'Consent Start Download',
  consentDownloadFailed: 'Consent Download Failed',
  completedConsentDelivered: 'Completed Consent Delivered',
  completedConsentPdfDownloaded: 'Completed Consent PDF Downloaded',
  completedConsentPdfDownloadFailed: 'Completed Consent PDF Download Failed',
  completedConsentsListDelivered: 'Completed Consents List Delivered',
  completedConsentsListFetchFailed: 'Completed Consents List Fetch Failed',
  pendingConsentsFetchFailed: 'Error fetching pending consents',
  signIn: 'Sign In',
  signOut: 'Sign Out',
  uiSignIn: 'UI Sign In',
  uiSignOut: 'UI Sign Out',
  loginScreenViewed: 'Login Screen Viewed',
  loginFailed: 'Login Failed',
  tokenFailure: 'Token Failure',
  missingAuthToken: 'Error missing auth token',
  appLaunch: 'App Launch',
  appTerminated: 'App Terminated',
  enterForeground: 'Enter Foreground Mode',
  enterBackground: 'Enter Background Mode',
  appSessionInvalid: 'App Session Invalid Error',
  announcementDelivered: 'Announcement Delivered',
  announcementVisible: 'Announcement Visible',
  announcementSeen: 'Announcement Seen',
  announcementRead: 'Announcement Read',
  calendarOpened: 'Calendar Opened',
  visitsTimelineFetchFailed: 'visits_timeline_fetch_failed',
  permissionAccepted: 'Permission Accepted',
  permissionDenied: 'Permission Denied',
  permissionGranted: 'Permission Granted',
  permissionError: 'Permission Error',
  creditTransaction: 'Credit Transaction',
  userProfileScreenViewed: 'user_profile_screen_viewed',
}

const toPrimitive = value => {
  if (value === undefined || value === null || value === '') return undefined
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'object') return undefined
  return String(value)
}

const toId = value => {
  if (value === undefined || value === null || value === '') return undefined
  return String(value)
}

export const getParticipantLogContext = () => {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEYS.user)
    const user = raw ? JSON.parse(raw) || {} : {}
    const studyId = user?.study_id ?? user?.study?.id
    const selectedPtpId = user?.is_caregiver
      ? getCaregiverSelectedParticipantId()
      : undefined
    const participantId =
      selectedPtpId || (user?.id !== undefined && user?.id !== null
        ? String(user.id)
        : undefined)
    const username = user?.username || user?.name || user?.email
    return {
      study_id: toId(studyId),
      participant_id: participantId,
      device_id: getHpiHardwareIdHeaderValue(username),
    }
  } catch (_) {
    return {}
  }
}

const collectAttributes = (tags = {}, contexts = {}) => {
  const attrs = {
    application: 'Linkt Web',
    ...getParticipantLogContext(),
  }

  Object.entries(tags || {}).forEach(([key, value]) => {
    const primitive = toPrimitive(value)
    if (primitive !== undefined) attrs[key] = primitive
  })

  Object.entries(contexts || {}).forEach(([ctxKey, ctxVal]) => {
    if (!ctxVal || typeof ctxVal !== 'object' || Array.isArray(ctxVal)) return
    Object.entries(ctxVal).forEach(([key, value]) => {
      const primitive = toPrimitive(value)
      if (primitive !== undefined) attrs[`${ctxKey}.${key}`] = primitive
    })
  })

  return attrs
}

const applyScopeMetadata = (scope, tags, contexts, level) => {
  scope.setTag('application', 'Linkt Web')
  Object.entries(tags || {}).forEach(([key, value]) => {
    const primitive = toPrimitive(value)
    if (primitive !== undefined) scope.setTag(key, primitive)
  })
  Object.entries(contexts || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      scope.setContext(key, value)
    }
  })
  scope.setLevel(level)
}

export const initSentry = () => {
  Sentry.init({
    dsn:
      'https://f708cbb395983736db14f79b336328b2@o1220724.ingest.us.sentry.io/4506904488574976',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
      Sentry.captureConsoleIntegration({ levels: ['error', 'warn'] }),
    ],
    tracesSampleRate: 1.0,
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/admin\.us\.datacubed\.com/,
    ],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    enableLogs: true,
    beforeSendLog: log => {
      if (log.level === 'debug' || log.level === 'trace') return null
      return log
    },
  })
}

export const setSentryUser = user => {
  try {
    if (user?.id === undefined || user?.id === null || user?.id === '') {
      return
    }
    Sentry.setUser({ id: String(user.id) })
  } catch (_) {
    /* never break the app */
  }
}

export const clearSentryUser = () => {
  try {
    Sentry.setUser(null)
  } catch (_) {
    /* never break the app */
  }
}

export const logSentryEvent = ({
  message,
  level = 'info',
  tags = {},
  contexts = {},
  breadcrumb,
  error,
} = {}) => {
  try {
    const identity = getParticipantLogContext()
    const mergedTags = { ...identity, ...tags }
    const attrs = collectAttributes(mergedTags, contexts)
    const resolvedLevel = error ? 'error' : level
    const isFailure =
      Boolean(error) || resolvedLevel === 'error' || resolvedLevel === 'fatal'

    if (breadcrumb?.message) {
      Sentry.addBreadcrumb({
        category: breadcrumb.category || 'app',
        message: breadcrumb.message,
        level: breadcrumb.level || resolvedLevel,
      })
    } else if (message) {
      Sentry.addBreadcrumb({
        category: mergedTags.flow || 'app',
        message,
        level: resolvedLevel,
      })
    }

    if (isFailure) {
      Sentry.withScope(scope => {
        applyScopeMetadata(scope, mergedTags, contexts, resolvedLevel)
        if (error) {
          Sentry.captureException(error)
        } else {
          Sentry.captureMessage(message || 'Sentry event')
        }
      })
      Sentry.logger?.error?.(message || 'Sentry event', attrs)
      return
    }

    if (resolvedLevel === 'warn') {
      Sentry.logger?.warn?.(message || 'Sentry event', attrs)
      return
    }

    Sentry.logger?.info?.(message || 'Sentry event', attrs)
  } catch (_) {
    /* logging must never break participant flows */
  }
}

export const logInstrumentInstances = (
  message,
  groups = [],
  extraTags = {},
  { firstInstanceOnly = true } = {},
) => {
  try {
    ;(groups || []).forEach(group => {
      const instances = group?.instances || []
      const rows = firstInstanceOnly
        ? instances[0]
          ? [instances[0]]
          : [{}]
        : instances.length
          ? instances
          : [{}]
      rows.forEach(instance => {
        logSentryEvent({
          message,
          tags: {
            flow: 'instrument',
            instrument_id: toId(
              group?.instrument_id || group?.identity || group?.id,
            ),
            instance_id: toId(instance?.instance_id),
            instrument_type: group?.instrument_type || group?.type,
            ...extraTags,
          },
        })
      })
    })
  } catch (_) {
    /* never break the app */
  }
}

export const initSentryLifecycleListeners = () => {
  try {
    logSentryEvent({
      message: SENTRY_MESSAGES.appLaunch,
      tags: { flow: 'app', action: 'app_launch' },
    })

    document.addEventListener('visibilitychange', () => {
      const isVisible = document.visibilityState === 'visible'
      logSentryEvent({
        message: isVisible
          ? SENTRY_MESSAGES.enterForeground
          : SENTRY_MESSAGES.enterBackground,
        tags: {
          flow: 'app',
          action: isVisible ? 'enter_foreground' : 'enter_background',
        },
      })
    })

    window.addEventListener('pagehide', () => {
      logSentryEvent({
        message: SENTRY_MESSAGES.appTerminated,
        tags: { flow: 'app', action: 'app_terminated' },
      })
    })
  } catch (_) {
    /* never break the app */
  }
}
